'use client';

/**
 * Neural Web — 3D force graph of the whole empire.
 * BEAST core → category hubs → projects → hub sites & funnels.
 * Drag to rotate, scroll to zoom, click a node to fly to its page.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import * as THREE from 'three';
import SpriteText from 'three-spritetext';

const ForceGraph3D = dynamic(() => import('react-force-graph-3d'), { ssr: false });

type GNode = {
  id: string; label: string; kind: string; group: string;
  color: string; size: number; href?: string; meta?: string;
};
type GLink = { source: string; target: string; color?: string; width?: number; particles?: number };

const CAT_COLOR: Record<string, string> = {
  core: '#00f0ff',
  wtf: '#22d3ee',
  ventures: '#4ade80',
  brian: '#f472b6',
  amigo: '#60a5fa',
  homyak: '#2dd4bf',
  ivan: '#818cf8',
  vsl: '#fb923c',
  tools: '#c084fc',
  family: '#facc15',
  site: '#7dd3fc',
  funnel: '#f472b6',
  other: '#94a3b8',
};

export default function NetworkPage() {
  const router = useRouter();
  const [data, setData] = useState<{ nodes: GNode[]; links: GLink[] }>({ nodes: [], links: [] });
  const [status, setStatus] = useState('loading the web…');
  const [hover, setHover] = useState<GNode | null>(null);
  const fgRef = useRef<any>(null);
  const camSet = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  // hover highlight — ids of the hovered node + its direct neighbours
  const hotRef = useRef<Set<string>>(new Set());
  const [, bump] = useState(0);
  // auto-orbit pauses while the user is driving
  const idleAt = useRef(0);
  const adjRef = useRef<Record<string, Set<string>>>({});

  // Size the canvas to its container (not the window) so nothing hides under the sidebar.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setDims({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setDims({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const [projRes, sitesRes, funnelsRes, leadsRes] = await Promise.all([
          fetch('/api/projects').then(r => r.json()).catch(() => null),
          fetch('/api/hub/sites').then(r => r.json()).catch(() => null),
          fetch('/api/funnels').then(r => r.json()).catch(() => null),
          fetch('/api/hub/crm/all/leads?days=7').then(r => r.json()).catch(() => null),
        ]);
        if (dead) return;

        const leadsBySite: Record<string, number> = {};
        for (const l of leadsRes?.leads ?? []) {
          const s = l.site || l.siteSlug || '';
          if (s) leadsBySite[s] = (leadsBySite[s] || 0) + 1;
        }

        const nodes: GNode[] = [];
        const links: GLink[] = [];
        const have = new Set<string>();
        const add = (n: GNode) => { if (!have.has(n.id)) { have.add(n.id); nodes.push(n); } };

        add({ id: 'core', label: 'BEAST', kind: 'core', group: 'core', color: CAT_COLOR.core, size: 16, meta: 'command center', fx: 0, fy: 0, fz: 0 } as any);

        const projects = (projRes?.projects ?? []).filter((p: any) => p.status !== 'archived');

        // Category hubs pinned on a ring around the core — deterministic sectors,
        // not physics soup. Children spawn near their hub and stay in its sector.
        const cats = [...new Set(projects.map((p: any) => p.category || 'other'))] as string[];
        const CAT_LABEL: Record<string, string> = {
          wtf: 'WTF Sites', ventures: 'Ventures', brian: 'Brian', amigo: 'Amigo (Greg)',
          homyak: 'Homyak', ivan: 'Ivan', vsl: 'VSL', tools: 'Tools', family: 'Family',
        };
        const R = 300;
        const catPos: Record<string, [number, number, number]> = {};
        cats.forEach((c, i) => {
          const a = (i / cats.length) * Math.PI * 2;
          // alternate hubs above/below the equator for 3D volume
          const y = (i % 2 === 0 ? 1 : -1) * 80;
          catPos[c] = [Math.cos(a) * R, y, Math.sin(a) * R];
          const [fx, fy, fz] = catPos[c];
          add({ id: `cat:${c}`, label: CAT_LABEL[c] || c, kind: 'category', group: c, color: CAT_COLOR[c] || CAT_COLOR.other, size: 9, meta: `${projects.filter((p: any) => (p.category || 'other') === c).length} projects`, fx, fy, fz } as any);
          links.push({ source: 'core', target: `cat:${c}`, color: CAT_COLOR[c] || CAT_COLOR.other, width: 2.5, particles: 4 });
        });

        // place children on a small sphere around their pinned hub
        const childSeq: Record<string, number> = {};
        const nearHub = (c: string, radius: number) => {
          const n = (childSeq[c] = (childSeq[c] || 0) + 1);
          const [cx, cy, cz] = catPos[c] || [0, 0, 0];
          const ga = n * 2.39996; // golden angle spiral — even spread
          const gy = 1 - (n % 12) / 6;
          const r = radius * Math.sqrt(1 - gy * gy);
          return { x: cx + Math.cos(ga) * r, y: cy + gy * radius, z: cz + Math.sin(ga) * r };
        };

        const projByDomain: Record<string, string> = {};
        const projCat: Record<string, string> = {};
        for (const p of projects) {
          const c = p.category || 'other';
          const live = p.live === true || p.progress >= 100;
          const pos = nearHub(c, 85);
          add({
            id: `p:${p.slug}`, label: p.name, kind: 'project', group: c,
            href: `/projects/${p.slug}`,
            size: live ? 5 : 3.5,
            color: live ? CAT_COLOR[c] || CAT_COLOR.other : '#475569',
            live,
            meta: `${c} · ${p.status} · ${p.progress ?? 0}%${live ? ' · LIVE' : ''}`,
            ...pos,
          } as any);
          links.push({ source: `cat:${c}`, target: `p:${p.slug}`, color: 'rgba(148,163,184,0.35)' });
          projCat[`p:${p.slug}`] = c;
          if (p.url) projByDomain[p.url.replace(/^https?:\/\//, '').replace(/\/$/, '')] = `p:${p.slug}`;
        }

        // Hub sites attach to their project node (domain match) or the wtf hub
        for (const s of sitesRes?.sites ?? []) {
          const leads = leadsBySite[s.slug] ?? leadsBySite[s.domain] ?? 0;
          const parent = projByDomain[s.domain] ?? (have.has(`p:${s.slug}-ca`) ? `p:${s.slug}-ca` : 'cat:wtf');
          const pos = nearHub(projCat[parent] || 'wtf', 150);
          add({
            id: `s:${s.slug}`, label: s.domain, kind: 'site', group: 'site',
            href: '/seo', size: 3 + Math.min(4, Math.sqrt(leads)),
            color: leads > 0 ? '#34d399' : CAT_COLOR.site,
            live: leads > 0,
            meta: `hub site · ${leads} leads 7d`,
            ...pos,
          } as any);
          links.push({ source: parent, target: `s:${s.slug}`, color: 'rgba(125,211,252,0.3)', particles: leads > 0 ? 2 : 0 });
        }

        for (const f of funnelsRes?.funnels ?? []) {
          const pos = nearHub('vsl', 85);
          add({
            id: `f:${f.id}`, label: f.name, kind: 'funnel', group: 'funnel',
            href: '/funnels', size: 5, color: CAT_COLOR.funnel, meta: 'funnel · live sync', live: true,
            ...pos,
          } as any);
          links.push({ source: 'cat:vsl', target: `f:${f.id}`, color: 'rgba(244,114,182,0.5)', particles: 2 });
        }

        const adj: Record<string, Set<string>> = {};
        for (const l of links) {
          (adj[l.source] ||= new Set()).add(l.target);
          (adj[l.target] ||= new Set()).add(l.source);
        }
        adjRef.current = adj;
        setData({ nodes, links });
        setStatus(`${nodes.length} nodes · ${links.length} links · ${Object.values(leadsBySite).reduce((a, b) => a + b, 0)} leads this week`);
      } catch {
        if (!dead) setStatus('failed to load the web');
      }
    })();
    return () => { dead = true; };
  }, []);

  // ── one shared radial-gradient texture for every halo sprite ──────────────
  // built lazily on first use — touching `document` at render time breaks SSR
  const haloTexRef = useRef<THREE.CanvasTexture | null>(null);
  const getHaloTex = useCallback(() => {
    if (haloTexRef.current) return haloTexRef.current;
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const g = c.getContext('2d')!;
    const grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    grd.addColorStop(0, 'rgba(255,255,255,1)');
    grd.addColorStop(0.25, 'rgba(255,255,255,0.45)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, 128, 128);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    haloTexRef.current = t;
    return t;
  }, []);

  const nodeThreeObject = useCallback((node: any) => {
    const grp = new THREE.Group();
    const major = node.kind === 'core' || node.kind === 'category';

    // glow halo — additive sprite, this is what bloom grabs onto
    const halo = new THREE.Sprite(new THREE.SpriteMaterial({
      map: getHaloTex(),
      color: new THREE.Color(node.color),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      opacity: node.kind === 'core' ? 0.55 : major ? 0.8 : 0.5,
    }));
    // the core is the biggest node — give it a *tighter* halo or it blows out
    // into a white rectangle instead of reading as a star
    const base = (node.size || 3) * (node.kind === 'core' ? 3 : major ? 4.2 : 3.2);
    halo.scale.set(base, base, 1);
    (halo as any).__base = base;
    (halo as any).__phase = Math.random() * Math.PI * 2;
    (halo as any).__pulse = major || node.live ? 1 : 0;
    grp.add(halo);
    (grp as any).__halo = halo;

    // Labels only where they earn their pixels: core, hubs, and anything live.
    // Everything else gets its name on hover — that's what killed readability before.
    if (major || node.live) {
      const sprite = new SpriteText(node.label);
      sprite.color = node.color;
      sprite.fontSize = 110;               // big source canvas = crisp text when zoomed
      sprite.fontFace = 'Inter, system-ui, sans-serif';
      sprite.textHeight = node.kind === 'core' ? 16 : node.kind === 'category' ? 10 : 4;
      sprite.fontWeight = major ? 'bold' : '500';
      sprite.backgroundColor = 'rgba(8,12,20,0.6)';
      sprite.padding = major ? 2.5 : 1.5;
      sprite.borderRadius = 3;
      sprite.position.y = (node.size || 3) + (node.kind === 'core' ? 16 : major ? 9 : 4);
      grp.add(sprite);
    }
    return grp;
  }, [getHaloTex]);

  // ── HD renderer + bloom + the animation loop that makes it breathe ────────
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg || !data.nodes.length) return;
    let dead = false;
    let raf = 0;

    (async () => {
      const [{ UnrealBloomPass }, { OutputPass }] = await Promise.all([
        import('three/examples/jsm/postprocessing/UnrealBloomPass.js'),
        import('three/examples/jsm/postprocessing/OutputPass.js'),
      ]);
      if (dead || !fgRef.current) return;
      const g = fgRef.current;

      // high definition: render at the display's real pixel density
      try {
        const r = g.renderer();
        r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        // no tone mapping — ACES lifts the blacks and the void goes navy
        r.toneMapping = THREE.NoToneMapping;
      } catch {}

      try {
        const comp = g.postProcessingComposer();
        if (comp && !comp.__bloom) {
          // strength / radius / threshold — tight and selective: a wide radius
          // smears glow across the whole frame and greys out the void
          const bloom = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.85, 0.32, 0.34);
          comp.addPass(bloom);
          // without an explicit output pass the composer leaves the frame in
          // linear space and the whole void washes out to navy
          comp.addPass(new OutputPass());
          comp.__bloom = bloom;
        }
      } catch {}

      // forces: children float free around their pinned hub instead of sitting
      // frozen on the spawn point — this is what makes the web actually move
      try {
        g.d3Force('charge')?.strength((n: any) => (n.kind === 'category' ? -420 : -70)).distanceMax(600);
        g.d3Force('link')?.distance((l: any) => {
          const t = typeof l.target === 'object' ? l.target : null;
          return t?.kind === 'category' ? 300 : t?.kind === 'site' ? 70 : 55;
        }).strength(0.55);
      } catch {}

      const t0 = performance.now();
      const tick = () => {
        if (dead) return;
        raf = requestAnimationFrame(tick);
        const t = (performance.now() - t0) / 1000;

        // halo pulse — core and hubs breathe, live nodes shimmer
        for (const n of (g.graphData?.().nodes ?? [])) {
          const halo = (n as any).__threeObj?.__halo;
          if (!halo || !halo.__pulse) continue;
          const k = 1 + 0.14 * Math.sin(t * 1.6 + halo.__phase);
          halo.scale.set(halo.__base * k, halo.__base * k, 1);
        }

        // slow auto-orbit, but only when the user isn't driving
        if (performance.now() > idleAt.current) {
          try {
            const cam = g.camera();
            const a = 0.0011;
            const x = cam.position.x * Math.cos(a) - cam.position.z * Math.sin(a);
            const z = cam.position.x * Math.sin(a) + cam.position.z * Math.cos(a);
            cam.position.x = x; cam.position.z = z;
            cam.lookAt(0, 0, 0);
          } catch {}
        }
      };
      raf = requestAnimationFrame(tick);
    })();

    return () => { dead = true; cancelAnimationFrame(raf); };
  }, [data]);

  // any pointer/wheel input parks the auto-orbit for 5s
  useEffect(() => {
    const park = () => { idleAt.current = performance.now() + 5000; };
    const el = wrapRef.current;
    el?.addEventListener('pointerdown', park);
    el?.addEventListener('wheel', park, { passive: true });
    return () => { el?.removeEventListener('pointerdown', park); el?.removeEventListener('wheel', park); };
  }, []);

  const onHover = useCallback((n: any) => {
    const next = new Set<string>();
    if (n) { next.add(n.id); for (const id of adjRef.current[n.id] ?? []) next.add(id); }
    hotRef.current = next;
    setHover(n || null);
    bump(v => v + 1);
  }, []);

  const dim = (hex: string, node: any) =>
    !hotRef.current.size || hotRef.current.has(node.id) ? hex : 'rgba(80,95,120,0.25)';

  return (
    <div ref={wrapRef} className="relative h-[calc(100vh-0px)] overflow-hidden">
      <ForceGraph3D
        ref={fgRef}
        width={dims.w || undefined}
        height={dims.h || undefined}
        graphData={data}
        backgroundColor="#05080f"
        nodeThreeObject={nodeThreeObject}
        nodeThreeObjectExtend={true}
        nodeVal={(n: any) => n.size}
        nodeColor={(n: any) => dim(n.color, n)}
        nodeOpacity={0.95}
        nodeResolution={32}
        linkColor={(l: any) => {
          if (!hotRef.current.size) return l.color || 'rgba(148,163,184,0.25)';
          const s = typeof l.source === 'object' ? l.source.id : l.source;
          const t = typeof l.target === 'object' ? l.target.id : l.target;
          return hotRef.current.has(s) && hotRef.current.has(t) ? '#ffffff' : 'rgba(80,95,120,0.12)';
        }}
        linkWidth={(l: any) => {
          const s = typeof l.source === 'object' ? l.source.id : l.source;
          const t = typeof l.target === 'object' ? l.target.id : l.target;
          const hot = hotRef.current.size && hotRef.current.has(s) && hotRef.current.has(t);
          return hot ? (l.width || 0.6) * 2.5 : l.width || 0.6;
        }}
        linkOpacity={0.5}
        linkDirectionalParticles={(l: any) => l.particles || 1}
        linkDirectionalParticleWidth={(l: any) => (l.particles ? 2.2 : 1.1)}
        linkDirectionalParticleSpeed={0.004}
        linkDirectionalParticleResolution={8}
        onNodeHover={onHover}
        onNodeClick={(n: any) => { if (n?.href) router.push(n.href); }}
        showNavInfo={false}
        warmupTicks={40}
        d3AlphaDecay={0.018}
        d3VelocityDecay={0.32}
        d3AlphaMin={0.004}
        cooldownTime={Infinity}
        onEngineTick={() => {
          if (camSet.current) return;
          camSet.current = true;
          // fit twice — the layout is still expanding on the first one
          const fit = (ms: number) => setTimeout(() => {
            if (performance.now() <= idleAt.current) return;
            try {
              const g = fgRef.current;
              g.zoomToFit(1200, 30);
              // zoomToFit is conservative on a 3D cloud — pull in so the web
              // actually fills the canvas instead of floating in a void
              setTimeout(() => {
                if (performance.now() <= idleAt.current) return;
                const c = g.camera();
                const k = 0.62;
                g.cameraPosition({ x: c.position.x * k, y: c.position.y * k, z: c.position.z * k }, { x: 0, y: 0, z: 0 }, 900);
              }, 1300);
            } catch {}
          }, ms);
          fit(1500); fit(6000);
        }}
      />

      {/* HUD */}
      <div className="absolute top-4 left-4 pointer-events-none">
        <h1 className="text-lg font-semibold text-white">Neural Web</h1>
        <p className="text-xs text-gray-500">{status}</p>
        <p className="text-[10px] text-gray-600 mt-1">drag = rotate · scroll = zoom · right-drag = pan · hover = trace · click = open</p>
      </div>

      {hover && (
        <div className="absolute bottom-6 left-4 px-3 py-2 rounded-xl bg-black/70 border border-white/10 pointer-events-none">
          <div className="text-sm font-medium" style={{ color: hover.color }}>{hover.label}</div>
          {hover.meta && <div className="text-xs text-gray-400">{hover.meta}</div>}
        </div>
      )}

      {/* Legend */}
      <div className="absolute top-4 right-4 flex flex-col gap-1 px-3 py-2 rounded-xl bg-black/50 border border-white/[0.06]">
        {Object.entries({ 'BEAST core': CAT_COLOR.core, 'WTF sites': CAT_COLOR.wtf, Ventures: CAT_COLOR.ventures, Brian: CAT_COLOR.brian, 'Amigo': CAT_COLOR.amigo, Ivan: CAT_COLOR.ivan, 'VSL / funnels': CAT_COLOR.funnel, Tools: CAT_COLOR.tools, 'Hub site (green = leads)': '#34d399' }).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-[10px] text-gray-400">
            <span className="w-2 h-2 rounded-full" style={{ background: v }} /> {k}
          </div>
        ))}
      </div>
    </div>
  );
}
